import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Upload, Download, Archive, FileText, ExternalLink, Trash2 } from "lucide-react";

const DOC_TYPES = [["proposal", "Proposal"], ["purchase_order", "Purchase Order"], ["work_order", "Work-Order Request"],
  ["agreement", "Agreement / Contract"], ["other", "Other"]];
const STATUS_STYLES = { prospective: "badge-submitted", active: "badge-approved", past: "badge-neutral", inactive: "badge-nosend" };

function DocUpload({ orgId, schools, engagements, proposals, onSaved, onCancel }) {
  const [f, setF] = useState({ title: "", doc_type: "proposal", school_id: "", engagement_id: "", proposal_id: "",
    reference_number: "", document_date: "", service_period: "", amount: "", currency: "USD", status: "", notes: "", version_label: "", external_url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("organization_id", orgId);
      Object.entries(f).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      await api.post("/admin/client-documents", fd);
      toast.success("Document uploaded. Uploads never approve proposals, change budgets, or mark purchase orders paid.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card p-5 space-y-3 fade-up" data-testid="client-doc-upload-form">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div><label className="label">Title *</label>
          <input className="input" value={f.title} data-testid="cdoc-title" onChange={(e) => setF({ ...f, title: e.target.value })} /></div>
        <div><label className="label">Type</label>
          <select className="input" value={f.doc_type} data-testid="cdoc-type" onChange={(e) => setF({ ...f, doc_type: e.target.value })}>
            {DOC_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <div><label className="label">Link to school (optional)</label>
          <select className="input" value={f.school_id} data-testid="cdoc-school" onChange={(e) => setF({ ...f, school_id: e.target.value })}>
            <option value="">District level</option>
            {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
        <div><label className="label">Related engagement</label>
          <select className="input" value={f.engagement_id} data-testid="cdoc-engagement" onChange={(e) => setF({ ...f, engagement_id: e.target.value })}>
            <option value="">None</option>
            {engagements.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}</select></div>
        <div><label className="label">Related proposal</label>
          <select className="input" value={f.proposal_id} data-testid="cdoc-proposal" onChange={(e) => setF({ ...f, proposal_id: e.target.value })}>
            <option value="">None</option>
            {proposals.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}</select></div>
        <div><label className="label">Reference / Purchase Order number</label>
          <input className="input" value={f.reference_number} data-testid="cdoc-reference" onChange={(e) => setF({ ...f, reference_number: e.target.value })} /></div>
        <div><label className="label">Document date</label>
          <input type="date" className="input" value={f.document_date} data-testid="cdoc-date" onChange={(e) => setF({ ...f, document_date: e.target.value })} /></div>
        <div><label className="label">Service period</label>
          <input className="input" placeholder="e.g. Fall 2026" value={f.service_period} data-testid="cdoc-period" onChange={(e) => setF({ ...f, service_period: e.target.value })} /></div>
        <div className="grid grid-cols-2 gap-2">
          <div><label className="label">Amount</label>
            <input type="number" min="0" step="0.01" className="input" value={f.amount} data-testid="cdoc-amount" onChange={(e) => setF({ ...f, amount: e.target.value })} /></div>
          <div><label className="label">Currency</label>
            <input className="input" value={f.currency} data-testid="cdoc-currency" onChange={(e) => setF({ ...f, currency: e.target.value })} /></div>
        </div>
        <div><label className="label">Status</label>
          <input className="input" placeholder="e.g. received, countersigned" value={f.status} data-testid="cdoc-status" onChange={(e) => setF({ ...f, status: e.target.value })} /></div>
        <div><label className="label">Version label</label>
          <input className="input" value={f.version_label} data-testid="cdoc-version" onChange={(e) => setF({ ...f, version_label: e.target.value })} /></div>
        <div><label className="label">File</label>
          <input type="file" className="input !py-2 text-xs" onChange={(e) => setFile(e.target.files?.[0])} data-testid="cdoc-file" /></div>
        <div className="lg:col-span-2"><label className="label">…or external link (e.g., Google Doc share link)</label>
          <input className="input" placeholder="https://docs.google.com/…" value={f.external_url} data-testid="cdoc-url"
            onChange={(e) => setF({ ...f, external_url: e.target.value })} /></div>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary" onClick={save} disabled={busy || !f.title || (!file && !f.external_url.trim())} data-testid="cdoc-save">{busy ? "Saving…" : "Add document"}</button>
        <button className="btn-ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { orgId } = useParams();
  const [c, setC] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [notes, setNotes] = useState("");

  const load = () => api.get(`/admin/clients/${orgId}`).then((r) => { setC(r.data); setNotes(r.data.internal_notes || ""); });
  useEffect(() => { load(); }, [orgId]);

  if (!c) return <div className="text-sm text-[#78716C] animate-pulse" data-testid="client-detail-loading">Loading client…</div>;

  const setStatus = async (client_status) => {
    await api.patch(`/admin/clients/${orgId}`, { client_status });
    load(); toast.success("Client status updated.");
  };
  const saveNotes = async () => {
    await api.patch(`/admin/clients/${orgId}`, { internal_notes: notes });
    toast.success("Internal notes saved (Admin-only).");
  };

  const activeDocs = c.documents.filter((d) => !d.archived);

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      <div className="fade-up">
        <Link to="/clients" className="text-sm font-bold text-[#78716C] hover:text-navy flex items-center gap-1 mb-3" data-testid="back-to-clients-link">
          <ArrowLeft size={14} /> Client Database</Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="section-title text-3xl">{c.name}</h1>
          <span className={STATUS_STYLES[c.client_status] || "badge-neutral"} data-testid="client-status-badge">{c.client_status}</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-3" data-testid="client-status-actions">
          {["prospective", "active", "past", "inactive"].filter((s) => s !== c.client_status).map((s) => (
            <button key={s} className="btn-ghost !py-1 !px-2.5 text-xs border border-[#E7E1D4]" data-testid={`set-status-${s}`}
              onClick={() => setStatus(s)}>Mark {s}</button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 fade-up-1">
        <div className="card p-5" data-testid="client-schools-card">
          <h2 className="font-serif font-bold text-lg text-navy mb-3">Schools & locations</h2>
          <ul className="space-y-2">
            {c.schools.map((s) => (
              <li key={s.id} className="text-sm" data-testid={`client-school-${s.id}`}>
                <b className="text-navy">{s.name}</b>
                {s.address && <span className="text-xs text-[#78716C] block">{s.address}</span>}
              </li>
            ))}
            {c.schools.length === 0 && <li className="text-sm text-[#78716C]">No schools.</li>}
          </ul>
        </div>
        <div className="card p-5" data-testid="client-contacts-card">
          <h2 className="font-serif font-bold text-lg text-navy mb-3">Contacts</h2>
          <ul className="space-y-2">
            {c.contacts.map((ct) => (
              <li key={ct.id} className="text-sm flex flex-wrap items-baseline gap-2" data-testid={`client-contact-${ct.id}`}>
                <b className="text-navy">{ct.first_name} {ct.last_name}</b>
                <span className="text-xs text-[#78716C]">{ct.email}</span>
                <span className="text-xs text-[#A8A29E]">{(ct.associations || []).map((a) => a.role.replace(/_/g, " ")).join(", ")}</span>
                <span className={ct.status === "active" ? "badge-approved" : "badge-neutral"}>{ct.status}</span>
              </li>
            ))}
            {c.contacts.length === 0 && <li className="text-sm text-[#78716C]">No contacts.</li>}
          </ul>
        </div>
      </div>

      <div className="card p-5 fade-up-1" data-testid="client-internal-notes-card">
        <h2 className="font-serif font-bold text-lg text-navy mb-2">Internal notes <span className="badge-nosend ml-1">Admin only</span></h2>
        <textarea className="input min-h-[90px]" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="client-internal-notes"
          placeholder="Never visible to consultants or clients…" />
        <button className="btn-outline mt-2 !py-1.5" onClick={saveNotes} data-testid="client-notes-save">Save notes</button>
      </div>

      <div className="card p-5 fade-up-2" data-testid="client-engagements-card">
        <h2 className="font-serif font-bold text-lg text-navy mb-3">Related engagements & proposals</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <div className="label">Engagements</div>
            <ul className="space-y-1.5">
              {c.engagements.map((e) => (
                <li key={e.id}><Link to={`/engagements/${e.id}`} className="text-sm font-bold text-navy hover:text-terracotta" data-testid={`client-engagement-${e.id}`}>
                  {e.title} <span className="text-xs font-normal text-[#78716C]">({e.status})</span></Link></li>
              ))}
              {c.engagements.length === 0 && <li className="text-xs text-[#A8A29E]">None yet.</li>}
            </ul>
          </div>
          <div>
            <div className="label">Proposals</div>
            <ul className="space-y-1.5">
              {c.proposals.map((p) => (
                <li key={p.id}><Link to={`/proposals/${p.id}`} className="text-sm font-bold text-navy hover:text-terracotta" data-testid={`client-proposal-${p.id}`}>
                  {p.title} <span className="text-xs font-normal text-[#78716C]">({p.status}{p.outcome?.status !== "none" ? ` · ${p.outcome.status}` : ""})</span></Link></li>
              ))}
              {c.proposals.length === 0 && <li className="text-xs text-[#A8A29E]">None yet — create one in the Proposal Generator.</li>}
            </ul>
          </div>
        </div>
      </div>

      <div className="card p-5 space-y-4 fade-up-2" data-testid="client-documents-card">
        <div className="flex items-center justify-between">
          <h2 className="font-serif font-bold text-lg text-navy">Documents</h2>
          <button className="btn-outline !py-1.5" onClick={() => setShowUpload(!showUpload)} data-testid="client-doc-upload-toggle">
            <Upload size={14} /> Upload document</button>
        </div>
        <p className="text-xs text-[#78716C]">Proposals, purchase orders, work-order requests, agreements, and other client documents stay distinct record types. Uploading a purchase order does not approve a proposal, change an engagement budget, or mark anything paid.</p>
        {showUpload && <DocUpload orgId={orgId} schools={c.schools} engagements={c.engagements} proposals={c.proposals}
          onSaved={() => { setShowUpload(false); load(); }} onCancel={() => setShowUpload(false)} />}
        <div className="space-y-2">
          {activeDocs.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E7E1D4] px-4 py-3" data-testid={`client-doc-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={16} className="text-terracotta shrink-0" />
                <div className="min-w-0">
                  <div className="text-sm font-bold text-navy flex items-center gap-2 flex-wrap">
                    {d.title}
                    <span className="badge-neutral">{DOC_TYPES.find(([k]) => k === d.doc_type)?.[1] || d.doc_type}</span>
                    {d.status && <span className="badge-submitted">{d.status}</span>}
                    {d.version_label && <span className="badge-neutral">{d.version_label}</span>}
                  </div>
                  <div className="text-[11px] text-[#78716C]">
                    {[d.reference_number && `Ref ${d.reference_number}`, d.document_date, d.service_period,
                      d.amount != null && `${d.currency} ${d.amount.toLocaleString()}`].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                {d.has_file !== false && d.storage_path && (
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/client-documents/${d.id}/file`} target="_blank" rel="noreferrer"
                    className="btn-ghost !py-1 !px-2 text-xs" data-testid={`client-doc-download-${d.id}`}><Download size={13} /></a>)}
                {d.external_url && <a href={d.external_url} target="_blank" rel="noreferrer" className="btn-ghost !py-1 !px-2 text-xs"><ExternalLink size={13} /></a>}
                <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" data-testid={`client-doc-archive-${d.id}`}
                  onClick={async () => { await api.patch(`/admin/client-documents/${d.id}`, { archive: true }); load(); toast.success("Archived — history preserved."); }}>
                  <Archive size={13} /></button>
                <button className="btn-ghost !py-1 !px-2 text-xs text-[#991B1B]" data-testid={`client-doc-delete-${d.id}`}
                  title="Delete document"
                  onClick={async () => {
                    if (!window.confirm(`Permanently delete "${d.title}"? This cannot be undone.`)) return;
                    try { await api.delete(`/admin/client-documents/${d.id}`); load(); toast.success("Document deleted."); }
                    catch (e) { toast.error(apiError(e)); }
                  }}>
                  <Trash2 size={13} /></button>
              </div>
            </div>
          ))}
          {activeDocs.length === 0 && <p className="text-sm text-[#78716C]" data-testid="client-docs-empty">No documents yet.</p>}
        </div>
      </div>
    </div>
  );
}
